import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  API_get_timecard_hours_split,
  API_get_actual_sales_daily,
  API_get_forecasted_sales_daily,
} from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  formatCurrency,
  formatHours,
  unwrapPayload,
} from "./_shared/laborMath";

// Pay multipliers match the Daily Punch Audit / Paylocity export — same
// math at the day-of-business rollup level.
const PAY_MULTIPLIER = {
  regular: 1,
  overtime: 1.5,
  doubletime: 2,
  holiday: 1,
};

// API_get_*_sales_daily constants end with "?store_id=" — append directly.
function fetchStoreScopedRange(api, storeId, startStr, endStr) {
  const url = `${api}${storeId}&date_start=${startStr}&date_end=${endStr}`;
  return axios.get(url, getDefaultAxiosConfig());
}

function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function indexByDate(records) {
  const m = new Map();
  if (!Array.isArray(records)) return m;
  for (const r of records) {
    if (r?.date != null) m.set(r.date, r.value);
  }
  return m;
}

function emptyDayBucket(dateStr) {
  return {
    date: dateStr,
    reg: 0,
    ot: 0,
    dt: 0,
    hol: 0,
    total: 0,
    regPay: 0,
    otPay: 0,
    dtPay: 0,
    holPay: 0,
    specialPay: 0,
    totalPay: 0,
    forecastSales: null,
    actualSales: null,
  };
}

function payFor(hours, wage, kind) {
  if (hours == null || wage == null) return 0;
  const h = Number(hours);
  const w = Number(wage);
  if (!Number.isFinite(h) || !Number.isFinite(w) || w <= 0) return 0;
  return h * w * (PAY_MULTIPLIER[kind] ?? 1);
}

function laborPct(wages, sales) {
  if (sales == null || sales <= 0) return null;
  return (wages / sales) * 100;
}

function pctOrDash(pct) {
  if (pct == null) return "—";
  return `${pct.toFixed(1)}%`;
}

function NotAvailable() {
  return <span className="text-slate-300">—</span>;
}

export default function LaborSummaryReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
}) {
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, homeStore),
    [storeNames, homeStore]
  );

  const [weekStart, setWeekStart] = useState(() =>
    snapToWeekStart(new Date(), weekStartDayIndex)
  );

  useEffect(() => {
    setWeekStart((prev) => snapToWeekStart(prev, weekStartDayIndex));
  }, [weekStartDayIndex]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 6);
    return d;
  }, [weekStart]);

  const incrementWeek = useCallback((currentStart, direction) => {
    const next = new Date(currentStart);
    next.setDate(next.getDate() + direction * 7);
    setWeekStart(next);
  }, []);

  const setWeekStartDate = useCallback(
    (newDate) => setWeekStart(snapToWeekStart(newDate, weekStartDayIndex)),
    [weekStartDayIndex]
  );

  const weekDates = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
      }),
    [weekStart]
  );

  const startStr = useMemo(
    () => dayjs(weekStart).format("YYYY-MM-DD"),
    [weekStart]
  );
  const endStr = useMemo(() => dayjs(weekEnd).format("YYYY-MM-DD"), [weekEnd]);

  const [timecards, setTimecards] = useState([]);
  const [forecastByDate, setForecastByDate] = useState(new Map());
  const [actualSalesByDate, setActualSalesByDate] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      const results = await Promise.allSettled([
        getRightworkAPI(API_get_timecard_hours_split, {
          store_id: homeStore,
          date_start: startStr,
          date_end: endStr,
        }),
        fetchStoreScopedRange(
          API_get_actual_sales_daily,
          homeStore,
          startStr,
          endStr
        ),
        fetchStoreScopedRange(
          API_get_forecasted_sales_daily,
          homeStore,
          startStr,
          endStr
        ),
      ]);

      if (cancelled) return;

      const [hoursResult, actualSalesResult, forecastResult] = results;

      if (hoursResult.status === "rejected") {
        setError("Couldn't load labor data. Try again or pick another week.");
        setIsLoading(false);
        return;
      }
      const list = Array.isArray(hoursResult.value?.data?.timecards)
        ? hoursResult.value.data.timecards
        : [];
      setTimecards(list);

      setActualSalesByDate(
        actualSalesResult.status === "fulfilled"
          ? indexByDate(unwrapPayload(actualSalesResult.value))
          : new Map()
      );
      setForecastByDate(
        forecastResult.status === "fulfilled"
          ? indexByDate(unwrapPayload(forecastResult.value))
          : new Map()
      );

      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, startStr, endStr, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Aggregate per business date in one pass. Pay math mirrors the Daily
  // Punch Audit: Reg 1× / OT 1.5× / DT 2× / Hol 1×, plus Special at 1×
  // for MBP + SSP premium hours from the labor-law subcheckers.
  const aggregates = useMemo(() => {
    const byDate = new Map();
    for (const d of weekDates) {
      const dateStr = dayjs(d).format("YYYY-MM-DD");
      byDate.set(dateStr, emptyDayBucket(dateStr));
    }

    for (const tc of timecards) {
      const dateStr = tc.clock_in_date;
      if (!dateStr || !byDate.has(dateStr)) continue;

      const bucket = byDate.get(dateStr);
      const wageRaw =
        tc.effective_hourly_wage != null && tc.effective_hourly_wage !== ""
          ? Number(tc.effective_hourly_wage)
          : NaN;
      const wage = Number.isFinite(wageRaw) && wageRaw > 0 ? wageRaw : null;

      const reg = Number(tc.regular ?? 0);
      const ot = Number(tc.overtime ?? 0);
      const dt = Number(tc.doubletime ?? 0);
      const hol = Number(tc.holiday ?? 0);
      const mbp = Number(tc.meal_break_premium_hours ?? 0) || 0;
      // SSP hours intentionally not summed into Special $: SSP is paid at
      // minimum wage per the CA wage order, so multiplying by the regular
      // wage would overstate.

      bucket.reg += reg;
      bucket.ot += ot;
      bucket.dt += dt;
      bucket.hol += hol;
      bucket.total += Number(tc.total_paid_hours ?? reg + ot + dt + hol);

      const regPay = payFor(reg, wage, "regular");
      const otPay = payFor(ot, wage, "overtime");
      const dtPay = payFor(dt, wage, "doubletime");
      const holPay = payFor(hol, wage, "holiday");
      const mbpPay = wage != null && mbp > 0 ? mbp * wage : 0;
      const specialPay = mbpPay;

      bucket.regPay += regPay;
      bucket.otPay += otPay;
      bucket.dtPay += dtPay;
      bucket.holPay += holPay;
      bucket.specialPay += specialPay;
      bucket.totalPay += regPay + otPay + dtPay + holPay + specialPay;
    }

    // Stitch sales onto each day; missing days stay null.
    for (const [dateStr, bucket] of byDate) {
      const f = forecastByDate.get(dateStr);
      const a = actualSalesByDate.get(dateStr);
      bucket.forecastSales = f == null ? null : Number(f);
      bucket.actualSales = a == null ? null : Number(a);
    }

    const days = weekDates.map((d) =>
      byDate.get(dayjs(d).format("YYYY-MM-DD"))
    );

    const total = days.reduce(
      (acc, day) => {
        acc.reg += day.reg;
        acc.ot += day.ot;
        acc.dt += day.dt;
        acc.hol += day.hol;
        acc.total += day.total;
        acc.regPay += day.regPay;
        acc.otPay += day.otPay;
        acc.dtPay += day.dtPay;
        acc.holPay += day.holPay;
        acc.specialPay += day.specialPay;
        acc.totalPay += day.totalPay;
        acc.forecastSales =
          day.forecastSales == null
            ? acc.forecastSales
            : (acc.forecastSales ?? 0) + day.forecastSales;
        acc.actualSales =
          day.actualSales == null
            ? acc.actualSales
            : (acc.actualSales ?? 0) + day.actualSales;
        return acc;
      },
      {
        reg: 0,
        ot: 0,
        dt: 0,
        hol: 0,
        total: 0,
        regPay: 0,
        otPay: 0,
        dtPay: 0,
        holPay: 0,
        specialPay: 0,
        totalPay: 0,
        forecastSales: null,
        actualSales: null,
      }
    );

    return { days, total };
  }, [timecards, weekDates, forecastByDate, actualSalesByDate]);

  const filters = (
    <div className="flex items-center">
      <WeekSelector
        start_date={weekStart}
        end_date={weekEnd}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
      />
    </div>
  );

  const weekLabel = `${dayjs(weekStart).format("MMM D")} – ${dayjs(
    weekEnd
  ).format("MMM D, YYYY")}`;

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-700">{weekLabel}</h4>
            <p className="text-xs text-slate-500">
              Per-day Reg / OT / DT / Hol hour and dollar splits with FLSA
              multipliers, plus Special Pay (Meal Break Premiums at the
              regular rate; Split Shift Premium hours are tracked but their
              $ is excluded because SSP is paid at minimum wage in payroll),
              and labor % vs. forecast and actual sales. Verify SSP dollars,
              tips, and FLSA blended-rate adjustments for multi-role workweeks
              in payroll.
            </p>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2">Day</th>
                    <th className="text-right font-semibold px-3 py-2">Reg Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">OT Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">DT Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">Hol Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">Total Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">Reg $</th>
                    <th className="text-right font-semibold px-3 py-2">OT $</th>
                    <th className="text-right font-semibold px-3 py-2">DT $</th>
                    <th className="text-right font-semibold px-3 py-2">Hol $</th>
                    <th className="text-right font-semibold px-3 py-2">Special $</th>
                    <th className="text-right font-semibold px-3 py-2">Total $</th>
                    <th className="text-right font-semibold px-3 py-2">Proj Sales</th>
                    <th className="text-right font-semibold px-3 py-2">Act Sales</th>
                    <th className="text-right font-semibold px-3 py-2">Proj Labor %</th>
                    <th className="text-right font-semibold px-3 py-2">Act Labor %</th>
                    <th className="text-right font-semibold px-3 py-2">Tips</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregates.days.map((day) => (
                    <tr
                      key={day.date}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        <div>{dayjs(day.date).format("ddd")}</div>
                        <div className="text-[10px] text-slate-500">
                          {dayjs(day.date).format("M/D")}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(day.reg)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(day.ot)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(day.dt)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(day.hol)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-800">
                        {formatHours(day.total)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(day.regPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(day.otPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(day.dtPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(day.holPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {day.specialPay > 0
                          ? formatCurrency(day.specialPay)
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-900">
                        {formatCurrency(day.totalPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {day.forecastSales == null
                          ? "—"
                          : formatCurrency(day.forecastSales)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {day.actualSales == null
                          ? "—"
                          : formatCurrency(day.actualSales)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {pctOrDash(laborPct(day.totalPay, day.forecastSales))}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {pctOrDash(laborPct(day.totalPay, day.actualSales))}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-400">
                        <NotAvailable />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-200">
                    <td className="px-3 py-2 font-bold text-slate-900">
                      Week Total
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(aggregates.total.reg)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(aggregates.total.ot)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(aggregates.total.dt)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(aggregates.total.hol)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(aggregates.total.total)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(aggregates.total.regPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(aggregates.total.otPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(aggregates.total.dtPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(aggregates.total.holPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {aggregates.total.specialPay > 0
                        ? formatCurrency(aggregates.total.specialPay)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(aggregates.total.totalPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {aggregates.total.forecastSales == null
                        ? "—"
                        : formatCurrency(aggregates.total.forecastSales)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {aggregates.total.actualSales == null
                        ? "—"
                        : formatCurrency(aggregates.total.actualSales)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {pctOrDash(
                        laborPct(
                          aggregates.total.totalPay,
                          aggregates.total.forecastSales
                        )
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {pctOrDash(
                        laborPct(
                          aggregates.total.totalPay,
                          aggregates.total.actualSales
                        )
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-400">
                      <NotAvailable />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </PrebuiltReportShell>
  );
}
